import { useEffect, type CSSProperties, type ReactNode } from "react";
import { useForm } from "@tanstack/react-form";
import { createRoute, redirect } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { UpdateNotificationSettingsInputDto } from "@carry/contract";
import { Route as RootRoute } from "./__root";
import { useAdminApi } from "../providers/api-context";
import { Button } from "@carry/ui";
import { Loader2, Save } from "lucide-react";

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/profile",
  beforeLoad: ({ context }) => {
    if (context.authLoading) return;
    if (!context.session) throw redirect({ to: "/auth/login" });
  },
  component: ProfilePage,
});

const toggleRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 14px",
  background: "var(--bg-raised)",
  border: "0.5px solid var(--rule-soft)",
  borderRadius: 2,
};

const toggleLabelStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--ink)",
};

const toggleDescStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--ink-3)",
  marginTop: 2,
};

export function ProfilePage() {
  const api = useAdminApi();
  const queryClient = useQueryClient();

  const { data: notificationData, isLoading: notificationLoading } = useQuery({
    queryKey: ["notification-settings"],
    queryFn: () => api.notificationSettings.get(),
  });

  const notificationSettings = notificationData?.data;

  const notificationUpdateMutation = useMutation({
    mutationFn: (input: UpdateNotificationSettingsInputDto) =>
      api.notificationSettings.update(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notification-settings"] });
      toast.success("通知設定を保存しました", { duration: 8000 });
    },
    onError: (err) => {
      toast.error("保存に失敗しました", {
        description: err instanceof Error ? err.message : String(err),
      });
    },
  });

  const notificationForm = useForm({
    defaultValues: {
      line_push_enabled: true as boolean,
    },
    onSubmit: async ({ value }) => {
      try {
        await notificationUpdateMutation.mutateAsync(value);
      } catch {
        // no-op (onError で処理済み)
      }
    },
  });

  useEffect(() => {
    if (notificationSettings) {
      notificationForm.reset({
        line_push_enabled: notificationSettings.line_push_enabled,
      });
    }
  }, [notificationSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ margin: -24 }}>
      <PageHeader />

      <div style={{ padding: "24px 32px 40px", maxWidth: 720 }}>
        <Section title="通知設定">
          <div style={{ fontSize: 11, color: "var(--ink-3)", marginBottom: -4, lineHeight: 1.6 }}>
            LINE Push 通知の受信を設定します。配送指示など重要な通知は設定に関わらず送信されます。
          </div>
          <notificationForm.Field name="line_push_enabled">
            {(f) => (
              <div style={toggleRowStyle}>
                <div>
                  <div style={toggleLabelStyle}>LINE Push 通知</div>
                  <div style={toggleDescStyle}>LINE のトーク画面にプッシュ通知を送信します。</div>
                </div>
                <input
                  type="checkbox"
                  aria-label="LINE Push 通知"
                  checked={f.state.value}
                  onChange={(e) => f.handleChange(e.target.checked)}
                  style={{ width: 16, height: 16, cursor: "pointer", flexShrink: 0 }}
                />
              </div>
            )}
          </notificationForm.Field>

          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <notificationForm.Subscribe selector={(s) => s.isSubmitting}>
              {(isSubmitting) => (
                <Button
                  type="button"
                  variant="default"
                  onClick={() => notificationForm.handleSubmit()}
                  disabled={notificationLoading || isSubmitting}
                >
                  {isSubmitting ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Save size={14} />
                  )}
                  通知設定保存
                </Button>
              )}
            </notificationForm.Subscribe>
          </div>
        </Section>
      </div>
    </div>
  );
}

function PageHeader() {
  return (
    <div
      className="cp-pageheader"
      style={{
        padding: "12px 32px",
        borderBottom: "0.5px solid var(--rule-softer)",
        background: "var(--bg)",
      }}
    />
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--ink-3)",
          marginBottom: 16,
          paddingBottom: 8,
          borderBottom: "0.5px solid var(--rule-softer)",
        }}
      >
        {title}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>{children}</div>
    </div>
  );
}
